import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, QrCode } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CT } from "@/lib/connectionsTheme";
import { QRCodeCard } from "@/components/org/intake/qr/QRCodeCard";
import { QRCodeCreateModal } from "@/components/org/intake/qr/QRCodeCreateModal";
import { QRCodePreviewModal } from "@/components/org/intake/qr/QRCodePreviewModal";

export const QRCodesTab = ({ canManage }) => {
  const { API } = useAuth();
  const [qrCodes, setQrCodes] = useState(null);
  const [activeModels, setActiveModels] = useState([]);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [previewQr, setPreviewQr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [qrRes, modelsRes] = await Promise.all([
        axios.get(`${API}/org/intake/qr-codes`, { withCredentials: true }),
        axios.get(`${API}/org/intake/models`, { withCredentials: true }),
      ]);
      setQrCodes(qrRes.data.qr_codes);
      setActiveModels(modelsRes.data.models.filter((m) => m.status === "active"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const hasActiveModel = activeModels.length > 0;

  return (
    <div className="space-y-4" data-testid="qr-codes-tab">
      <div className="flex items-center justify-between gap-3">
        <p className={CT.textSecondary}>Generate, brand and manage QR codes that link directly to your active Intake Models.</p>
        {canManage && hasActiveModel && (
          <button type="button" onClick={() => setCreateOpen(true)} data-testid="qr-create-btn" className={`${CT.btnPrimary} shrink-0`}>
            <Plus size={14} /> Generate QR Code
          </button>
        )}
      </div>

      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="qr-codes-error">{error}</p>}

      {!hasActiveModel && (
        <div className={`${CT.panel} flex flex-col items-center gap-3 p-10 text-center`} data-testid="qr-codes-empty-state">
          <QrCode size={28} className="text-slate-400 dark:text-slate-500" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Activate an Intake Model under the Intake Models tab to generate QR codes for it.</p>
        </div>
      )}

      {hasActiveModel && qrCodes && qrCodes.length === 0 && (
        <div className={`${CT.panel} flex flex-col items-center gap-3 p-10 text-center`} data-testid="qr-codes-none-yet">
          <QrCode size={28} className="text-slate-400 dark:text-slate-500" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No QR codes yet. Generate one to start distributing an Intake Model.</p>
        </div>
      )}

      {qrCodes && qrCodes.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="qr-codes-grid">
          {qrCodes.map((qr) => (
            <QRCodeCard key={qr.qr_id} qr={qr} canManage={canManage} onPreview={() => setPreviewQr(qr)} onChanged={load} />
          ))}
        </div>
      )}

      {createOpen && (
        <QRCodeCreateModal
          models={activeModels}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}

      {previewQr && (
        <QRCodePreviewModal
          qr={previewQr}
          onClose={() => setPreviewQr(null)}
          onChanged={(updated) => { setPreviewQr(updated); load(); }}
        />
      )}
    </div>
  );
};
